export type Role = 'director' | 'deputy_director' | 'chief_engineer' | 'designer'

export type DocType = 'ird' | 'pd' | 'rd' | 'gge'

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  ird: 'ИРД',
  pd: 'ПД',
  rd: 'РД',
  gge: 'ГГЭ',
}

export const DOC_TYPE_FULL_LABELS: Record<DocType, string> = {
  ird: 'Исходно-разрешительная документация',
  pd: 'Проектная документация',
  rd: 'Рабочая документация',
  gge: 'Государственная экспертиза',
}

export const DOC_TYPES: DocType[] = ['ird', 'pd', 'gge', 'rd']

export type SectionStatus =
  | 'not_started'
  | 'in_progress'
  | 'on_review'
  | 'suspended'
  | 'issued'
  | 'accepted'

export type ProjectColor = 'green' | 'yellow' | 'red' | 'grey'

export type ProjectLifecycleStage = 'pre_project' | 'in_design' | 'post_design'

// ---------- Auth ----------
export interface CurrentUser {
  id: string
  email: string
  full_name: string | null
  phone: string | null
  role: Role
  is_active: boolean
  profile_completed: boolean
}

// ---------- Game ----------
export interface ScoreOut {
  id: number
  nickname: string | null
  score: number
  achieved_at: string
}

// ---------- Users ----------
export interface UserOut {
  id: string
  email: string
  full_name: string | null
  role: Role
  is_active: boolean
  last_login_at: string | null
  created_at: string
  deleted_at: string | null
}

// ---------- Projects ----------
export interface ProjectListItem {
  id: string
  code: string
  name: string
  customer: string
  planned_end_date: string
  is_archived: boolean
  lifecycle_stage: ProjectLifecycleStage
  /** Эффективный цвет (manual_color если задан, иначе из view). */
  color: ProjectColor | null
  /** Ручной override цвета (null = авторасчёт). */
  manual_color: ProjectColor | null
  sections_total?: number
  sections_done?: number
  deleted_at?: string | null
}

export interface ProjectOut {
  id: string
  code: string
  name: string
  customer: string
  address: string
  start_date: string
  planned_end_date: string
  is_archived: boolean
  lifecycle_stage: ProjectLifecycleStage
  /** Эффективный цвет (manual_color если задан, иначе из view). */
  color: ProjectColor | null
  /** Ручной override цвета (null = авторасчёт). */
  manual_color: ProjectColor | null
  chief_engineer_id: string | null
  chief_engineer: { id: string; full_name: string; email: string; role: Role; is_active: boolean } | null
  substitute_gip_id: string | null
  substitute_gip: { id: string; full_name: string; email: string; role: Role; is_active: boolean } | null
  created_at: string
  /** Договорная стоимость проекта (только для директоров). */
  cost: string | null
  /** Примечание к проекту (свободный текст). */
  note: string | null
}

export interface ProjectCreate {
  code: string
  name: string
  customer: string
  address: string
  start_date: string
  planned_end_date: string
  chief_engineer_id?: string
  lifecycle_stage?: ProjectLifecycleStage
  /** Договорная стоимость проекта (необязательна). */
  cost?: string | null
  /** Примечание (необязательное). */
  note?: string | null
}

export interface ProjectPatch {
  code?: string
  name?: string
  customer?: string
  address?: string
  start_date?: string
  planned_end_date?: string
  chief_engineer_id?: string | null
  substitute_gip_id?: string | null
  member_ids?: string[]
  lifecycle_stage?: ProjectLifecycleStage
  /** null = снять ручной override, вернуть к авторасчёту */
  manual_color?: ProjectColor | null
  /** Договорная стоимость проекта. */
  cost?: string | null
  /** Примечание (пустая строка очищает). */
  note?: string | null
}

// ---------- Section Stages ----------
export interface SectionStageCreate {
  name: string
  start_date: string | null
  end_date: string | null
  /** Не передаётся ГИПом — бэкенд сохраняет стоимости, введённые директором */
  cost?: string | null
}

export interface SectionStageOut {
  id: string
  section_id: string
  sort_order: number
  name: string
  start_date: string | null
  end_date: string | null
  cost: string | null
  created_at: string
}

// ---------- Sections ----------
export interface SectionOut {
  id: string
  project_id: string
  code: string
  code_name?: string | null
  assignee_id: string | null
  assignee?: { id: string; full_name: string; email: string; role: Role; is_active: boolean } | null
  planned_start: string
  planned_end: string
  actual_end: string | null
  manual_progress: number | null
  status: SectionStatus
  note: string | null
  created_at: string
  updated_at: string | null
  doc_type: DocType
  cost: string | null
  parent_id: string | null
  depth: number
  sort_order: number
  name: string | null
  description: string | null
  /** Мягкое предупреждение: сумма стоимостей этапов больше стоимости раздела. */
  stage_cost_warning?: string | null
  stages: SectionStageOut[]
}

export interface SectionCreate {
  code: string
  assignee_id?: string
  planned_start: string
  planned_end: string
  note?: string
  doc_type?: DocType
  cost?: string | null
  parent_id?: string | null
  name?: string | null
  description?: string | null
  stages?: SectionStageCreate[]
}

export interface SectionPatch {
  assignee_id?: string | null
  planned_start?: string
  planned_end?: string
  actual_end?: string | null
  manual_progress?: number | null
  note?: string | null
  cost?: string | null
  sort_order?: number
  name?: string | null
  description?: string | null
  /** Если передан (даже пустым) — полностью заменяет этапы раздела */
  stages?: SectionStageCreate[]
}

// ---------- DocPhases ----------
export interface DocPhaseOut {
  id: string
  project_id: string
  doc_type: DocType
  start_date: string | null
  end_date: string | null
  cost: string | null
  updated_at: string | null
}

export interface DocPhasePatch {
  start_date?: string | null
  end_date?: string | null
  cost?: string | null
}

// ---------- Dashboard ----------
export interface DashboardKpi {
  total: number
  in_progress: number
  overdue: number
  done_month: number
}

export interface DashboardProject {
  id: string
  code: string
  name: string
  customer: string
  planned_end_date: string | null
  color: ProjectColor
  chief_name: string | null
}

export interface DeadlineItem {
  section_id: string
  section_code: string
  planned_end: string
  project_id: string
  project_name: string
  project_code: string
  assignee_name: string | null
}

export interface Dashboard {
  kpi: DashboardKpi
  projects: DashboardProject[]
  upcoming: DeadlineItem[]
  overdue: DeadlineItem[]
  as_of: string
}

// ---------- Dashboard Zones (новый API, ФТ-ЭКРАН-002) ----------
export interface ZoneProject {
  id: string
  code: string
  name: string
  customer: string
  start_date: string | null
  planned_end_date: string | null
  lifecycle_stage: ProjectLifecycleStage
  color: ProjectColor
  chief_name: string | null
  total_cost: string | null
  progress: number
  /** false: чужой проект (ГИП видит все проекты, чужие только на просмотр) */
  is_mine?: boolean
}

export interface Zone {
  count: number
  projects: ZoneProject[]
  enabled: boolean
}

export interface AttentionItem {
  section_id: string
  section_code: string
  planned_end: string
  status: SectionStatus
  doc_type: DocType
  progress: number
  project_id: string
  project_code: string
  project_name: string
  chief_name: string | null
  assignee_name: string | null
  is_overdue: boolean
  is_mine?: boolean
}

export interface DashboardZonesKpi {
  total: number
  in_progress: number
  pre_project: number
  post_design: number
  overdue_sections: number
  due_soon: number
  done_month: number
}

export interface DashboardZones {
  as_of: string
  kpi: DashboardZonesKpi
  zones: {
    pre_project: Zone
    in_design: Zone
    post_design: Zone
  }
  attention: AttentionItem[]
}

export interface GanttTask {
  id: string
  type: 'project' | 'doc_group' | 'section' | 'stage'
  name: string
  project_id: string
  project_code: string
  project_name: string
  code: string | null
  start: string   // '' for empty doc_group (no bar)
  end: string     // '' for empty doc_group (no bar)
  progress: number
  custom_class: 'bar-project' | 'bar-group' | 'bar-green' | 'bar-yellow' | 'bar-red' | 'bar-grey'
  // Цвет временной линии по состоянию сроков (для всех уровней: проект/группа/раздел)
  status_color?: 'red' | 'yellow' | 'green' | 'grey'
  // false: чужой проект ГИПа, показывается блеклым и ниже своих
  is_mine?: boolean
  // Project rows
  gip_name?: string | null
  // DocGroup rows
  doc_type?: string | null
  sections_count?: number | null
  // Section rows
  section_name?: string | null
  assignee_name?: string | null
  cost?: string | null
  // Stage rows (этап раздела): id родительского раздела в Ганте
  section_id?: string | null
}

// ---------- User Workload (страница сотрудника) ----------
export interface WorkloadProject {
  id: string
  code: string
  name: string
  planned_end_date: string | null
  start_date: string | null
  color: ProjectColor
  section_count: number
  overdue_count: number
}

export interface WorkloadSection {
  id: string
  code: string
  status: SectionStatus
  planned_end: string
  planned_start: string
  project_id: string
  project_code: string
  project_name: string
}

export interface UserWorkload {
  user: {
    id: string
    full_name: string | null
    email: string
    role: Role
    is_active: boolean
  }
  gip_projects: WorkloadProject[]
  assigned_sections: WorkloadSection[]
}

// ---------- Audit ----------
export interface AuditEntry {
  id: number
  entity_type: string
  entity_id: string
  user_id: string | null
  action: string
  action_label: string
  field_name: string | null
  old_value: unknown
  new_value: unknown
  ip_address: string | null
  created_at: string
}
